import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminService, type TeamStatus } from '@/services/admin';
import { seasonParticipationService } from '@/services/seasonParticipation';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';

export function SeasonManagementPage() {
  const [isCreatingseason, setIsCreatingSeason] = useState(false);
  const [selectedSeasonName, setSelectedSeasonName] = useState('');
  const [maxTeamPicks, setMaxTeamPicks] = useState(1);
  const [maxOppositionTargets, setMaxOppositionTargets] = useState(1);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: seasons, isLoading: loadingSeasons } = useQuery({
    queryKey: ['admin', 'seasons'],
    queryFn: () => adminService.getSeasons(),
  });

  // Seasons the current admin is already enrolled in (hide "Enroll Me" for those).
  const { data: myParticipations = [] } = useQuery({
    queryKey: ['my-participations'],
    queryFn: () => seasonParticipationService.getMyParticipations(),
  });
  const enrolledSeasonIds = new Set(myParticipations.map((p) => p.seasonId));

  const { data: teams, isLoading: loadingTeams } = useQuery({
    queryKey: ['admin', 'teams'],
    queryFn: () => adminService.getTeamStatuses(),
  });

  // A season's fixtures are released ~mid-June of its starting year (historically
  // 15-19 June, e.g. 2026/27 on 19 Jun 2026). There are no fixtures/teams to sync before
  // then, so only offer a season to create once its fixtures are out — the creatable
  // season rolls over in mid-June, not August.
  const today = new Date();
  const FIXTURES_RELEASE_MONTH = 5; // June (0-indexed)
  // Historical releases fell 15-19 June; use 20 so the season is only offered *after*
  // fixtures are actually out (tune this if a future release lands later).
  const FIXTURES_RELEASE_DAY = 20;
  const fixturesReleasedThisYear =
    today >= new Date(today.getFullYear(), FIXTURES_RELEASE_MONTH, FIXTURES_RELEASE_DAY);
  const availableStartYear = fixturesReleasedThisYear
    ? today.getFullYear()
    : today.getFullYear() - 1;

  // Offer only the season whose fixtures are available, excluding it if already created.
  // Hyphen format (e.g. "2026-2027") — a slash breaks seasonId-in-URL routing.
  const availableSeasons = [`${availableStartYear}-${availableStartYear + 1}`].filter(
    (seasonName) => !seasons?.some((s) => s.name === seasonName)
  );

  const createSeasonMutation = useMutation({
    mutationFn: adminService.createSeason,
    onSuccess: async (response) => {
      toast({
        title: 'Season Created Successfully',
        description: `Teams Created: ${response.teamsCreated}, Deactivated: ${response.teamsDeactivated}, Fixtures: ${response.fixturesCreated}`,
        duration: 7000,
      });

      // Create pick rules for first half
      try {
        await adminService.createPickRule({
          seasonId: response.seasonId,
          half: 1,
          maxTimesTeamCanBePicked: maxTeamPicks,
          maxTimesOppositionCanBeTargeted: maxOppositionTargets,
        });

        // Create pick rules for second half (same as first half by default)
        await adminService.createPickRule({
          seasonId: response.seasonId,
          half: 2,
          maxTimesTeamCanBePicked: maxTeamPicks,
          maxTimesOppositionCanBeTargeted: maxOppositionTargets,
        });

        toast({
          title: 'Pick Rules Created',
          description: `Pick rules set: Teams ${maxTeamPicks}x, Opposition ${maxOppositionTargets}x per half`,
        });
      } catch (error: unknown) {
        const err = error as { response?: { data?: { message?: string } }; message?: string };
        toast({
          title: 'Warning',
          description:
            'Season created but pick rules failed: ' + (err.response?.data?.message || err.message),
          variant: 'destructive',
        });
      }

      queryClient.invalidateQueries({ queryKey: ['admin', 'seasons'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'teams'] });
      queryClient.invalidateQueries({ queryKey: ['active-season'] });
      queryClient.invalidateQueries({ queryKey: ['season-approval'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      setIsCreatingSeason(false);
      setSelectedSeasonName('');
      setMaxTeamPicks(1);
      setMaxOppositionTargets(1);
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } };
      toast({
        title: 'Error',
        description: err.response?.data?.message || 'Failed to create season',
        variant: 'destructive',
      });
    },
  });

  const updateTeamStatusMutation = useMutation({
    mutationFn: ({ teamId, isActive }: { teamId: number; isActive: boolean }) =>
      adminService.updateTeamStatus(teamId, isActive),
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Team status updated',
      });
      queryClient.invalidateQueries({ queryKey: ['admin', 'teams'] });
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } };
      toast({
        title: 'Error',
        description: err.response?.data?.message || 'Failed to update team status',
        variant: 'destructive',
      });
    },
  });

  const syncTeamsMutation = useMutation({
    mutationFn: adminService.syncTeams,
    onSuccess: (response) => {
      toast({
        title: 'Teams Synced Successfully',
        description: `Created: ${response.teamsCreated}, Updated: ${response.teamsUpdated}, Active: ${response.totalActiveTeams}`,
      });
      queryClient.invalidateQueries({ queryKey: ['admin', 'teams'] });
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } };
      toast({
        title: 'Error',
        description: err.response?.data?.message || 'Failed to sync teams',
        variant: 'destructive',
      });
    },
  });

  const enrollAdminMutation = useMutation({
    mutationFn: (seasonId: string) => adminService.enrollAdminForSeason(seasonId),
    onSuccess: () => {
      toast({ title: 'Enrolled', description: 'You are now enrolled in this season.' });
      queryClient.invalidateQueries({ queryKey: ['my-participations'] });
      queryClient.invalidateQueries({ queryKey: ['league-standings'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } };
      toast({
        title: 'Error',
        description: err.response?.data?.message || 'Failed to enroll',
        variant: 'destructive',
      });
    },
  });

  const syncFixturesMutation = useMutation({
    mutationFn: (season?: number) => adminService.syncFixtures(season),
    onSuccess: (response) => {
      toast({
        title: 'Fixtures Synced Successfully',
        description: `Fixtures Created: ${response.fixturesCreated}, Updated: ${response.fixturesUpdated}, Gameweeks Created: ${response.gameweeksCreated}`,
      });
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } };
      toast({
        title: 'Error',
        description: err.response?.data?.message || 'Failed to sync fixtures',
        variant: 'destructive',
      });
    },
  });

  const syncResultsMutation = useMutation({
    mutationFn: adminService.syncResults,
    onSuccess: (response) => {
      toast({
        title: 'Results Synced Successfully',
        description: `Fixtures Updated: ${response.fixturesUpdated}, Gameweeks Processed: ${response.gameweeksProcessed}, Picks Recalculated: ${response.picksRecalculated}`,
        duration: 7000,
      });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['fixtures'] });
      queryClient.invalidateQueries({ queryKey: ['picks'] });
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } };
      toast({
        title: 'Error',
        description: err.response?.data?.message || 'Failed to sync results',
        variant: 'destructive',
      });
    },
  });

  const handleCreateSeason = () => {
    if (!selectedSeasonName) {
      toast({
        title: 'Validation Error',
        description: 'Please select a season',
        variant: 'destructive',
      });
      return;
    }

    // Extract the starting year from the season name (e.g., "2026-2027" -> 2026)
    const startYear = parseInt(selectedSeasonName, 10);
    const endYear = startYear + 1;

    // Create start and end dates (August 1st to May 31st)
    const startDate = new Date(Date.UTC(startYear, 7, 1)).toISOString(); // Month is 0-indexed, so 7 = August
    const endDate = new Date(Date.UTC(endYear, 4, 31)).toISOString(); // 4 = May

    createSeasonMutation.mutate({
      name: selectedSeasonName,
      startDate: startDate,
      endDate: endDate,
      externalSeasonYear: startYear,
    });
  };

  const handleToggleTeam = (team: TeamStatus) => {
    updateTeamStatusMutation.mutate({
      teamId: team.id,
      isActive: !team.isActive,
    });
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold">Season Management</h1>
        <p className="text-muted-foreground mt-2">Manage seasons, teams, and synchronize data</p>
      </div>

      {/* Create New Season Section */}
      <Card>
        <CardHeader>
          <CardTitle>Create New Season</CardTitle>
          <CardDescription>
            Create a new season and sync teams/fixtures from the Football Data API
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!isCreatingseason ? (
            availableSeasons.length > 0 ? (
              <Button onClick={() => setIsCreatingSeason(true)}>Create New Season</Button>
            ) : (
              <div className="space-y-2">
                <Button disabled>Create New Season</Button>
                <p className="text-sm text-muted-foreground" data-testid="no-season-available">
                  No new season to create yet — the latest season is already set up. A new season
                  becomes available once its fixtures are released (mid-June).
                </p>
              </div>
            )
          ) : createSeasonMutation.isPending ? (
            <div className="flex flex-col items-center justify-center p-8 space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              <div className="text-center">
                <p className="font-medium">Creating Season...</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Please wait while we set up the new season.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="seasonName">Select Season</Label>
                <select
                  id="seasonName"
                  value={selectedSeasonName}
                  onChange={(e) => setSelectedSeasonName(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="">-- Select a season --</option>
                  {availableSeasons.map((season) => (
                    <option key={season} value={season}>
                      {season}
                    </option>
                  ))}
                </select>
              </div>

              <div className="border-t pt-4 space-y-4">
                <h3 className="font-medium">Pick Rules (First Half)</h3>
                <p className="text-sm text-muted-foreground">
                  These rules will apply to both halves of the season. You can modify the second
                  half rules later if needed.
                </p>

                <div className="space-y-2">
                  <Label htmlFor="maxTeamPicks">Max times a team can be picked per half</Label>
                  <Input
                    id="maxTeamPicks"
                    type="number"
                    min="1"
                    max="19"
                    value={maxTeamPicks}
                    onChange={(e) => setMaxTeamPicks(parseInt(e.target.value) || 1)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Range: 1-19 (1 = each team once per half, 19 = allows picking every week)
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="maxOppositionTargets">
                    Max times an opposition can be targeted per half
                  </Label>
                  <Input
                    id="maxOppositionTargets"
                    type="number"
                    min="1"
                    max="19"
                    value={maxOppositionTargets}
                    onChange={(e) => setMaxOppositionTargets(parseInt(e.target.value) || 1)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Range: 1-19 (prevents repeatedly targeting weak teams)
                  </p>
                </div>
              </div>

              <div className="flex gap-2">
                <Button onClick={handleCreateSeason} disabled={!selectedSeasonName}>
                  Create Season
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsCreatingSeason(false);
                    setSelectedSeasonName('');
                    setMaxTeamPicks(1);
                    setMaxOppositionTargets(1);
                  }}
                >
                  Cancel
                </Button>
              </div>
              <div className="rounded-lg bg-blue-50 dark:bg-violet-950/40 p-4 border border-blue-200 dark:border-violet-800/60">
                <p className="text-sm font-medium text-blue-900 dark:text-violet-100 mb-2">
                  Steps after creating a season:
                </p>
                <ol className="text-sm text-blue-800 dark:text-violet-200 list-decimal list-inside space-y-1">
                  <li>Sync teams from the Football Data API (automatic)</li>
                  <li>Mark relegated teams as inactive in Team Status section</li>
                  <li>Sync fixtures for the new season (automatic)</li>
                  <li>Pick rules are created for both halves with your specified limits</li>
                </ol>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Existing Seasons */}
      <Card>
        <CardHeader>
          <CardTitle>Existing Seasons</CardTitle>
          <CardDescription>View and manage all seasons</CardDescription>
        </CardHeader>
        <CardContent>
          {loadingSeasons ? (
            <p>Loading seasons...</p>
          ) : seasons && seasons.length > 0 ? (
            <div className="space-y-2">
              {seasons.map((season) => (
                <div
                  key={season.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div>
                    <p className="font-medium">{season.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(season.startDate).toLocaleDateString()} -{' '}
                      {new Date(season.endDate).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {season.isActive && !enrolledSeasonIds.has(season.name) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => enrollAdminMutation.mutate(season.name)}
                        disabled={enrollAdminMutation.isPending}
                      >
                        Enroll Me
                      </Button>
                    )}
                    {season.isActive && (
                      <span className="px-2 py-1 text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded">
                        Active
                      </span>
                    )}
                    {season.isArchived && (
                      <span className="px-2 py-1 text-xs bg-gray-100 text-gray-800 dark:bg-secondary dark:text-foreground rounded">
                        Archived
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground">No seasons found</p>
          )}
        </CardContent>
      </Card>

      {/* Sync Operations */}
      <Card>
        <CardHeader>
          <CardTitle>Data Synchronization</CardTitle>
          <CardDescription>
            Sync teams, fixtures, and results from the Football Data API
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {syncTeamsMutation.isPending ||
          syncFixturesMutation.isPending ||
          syncResultsMutation.isPending ? (
            <div className="flex flex-col items-center justify-center p-8 space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              <div className="text-center">
                <p className="font-medium">
                  {syncTeamsMutation.isPending
                    ? 'Syncing Teams...'
                    : syncFixturesMutation.isPending
                      ? 'Syncing Fixtures...'
                      : 'Syncing Results...'}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  This may take a few moments. Please wait...
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => syncTeamsMutation.mutate()}
                  disabled={syncTeamsMutation.isPending}
                >
                  Sync Teams
                </Button>
                <Button
                  onClick={() => syncFixturesMutation.mutate(undefined)}
                  disabled={syncFixturesMutation.isPending}
                >
                  Sync Current Season Fixtures
                </Button>
                <Button
                  onClick={() => {
                    const year = prompt('Enter season year (e.g., 2025):');
                    if (year) syncFixturesMutation.mutate(parseInt(year));
                  }}
                  disabled={syncFixturesMutation.isPending}
                  variant="outline"
                >
                  Sync Specific Season
                </Button>
              </div>
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium mb-2">Match Results</h4>
                <Button
                  onClick={() => syncResultsMutation.mutate()}
                  disabled={syncResultsMutation.isPending}
                  variant="default"
                  className="bg-green-700 hover:bg-green-800"
                >
                  🔄 Sync Results & Update Points
                </Button>
                <p className="text-sm text-muted-foreground mt-2">
                  Updates fixture scores and recalculates all user points for recent gameweeks.
                </p>
              </div>
              <p className="text-sm text-muted-foreground">
                Sync teams first, then sync fixtures. Gameweeks will be created automatically. Use
                "Sync Results" after games finish to update scores.
              </p>
            </>
          )}
        </CardContent>
      </Card>

      {/* Team Status Management */}
      <Card>
        <CardHeader>
          <CardTitle>Team Status</CardTitle>
          <CardDescription>
            Manage which teams are active. When creating a new season, mark relegated teams as
            inactive before syncing fixtures.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingTeams ? (
            <p>Loading teams...</p>
          ) : teams && teams.length > 0 ? (
            <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
              {teams.map((team) => (
                <div
                  key={team.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    {team.logoUrl && <img src={team.logoUrl} alt={team.name} className="w-6 h-6" />}
                    <span className="text-sm font-medium">{team.name}</span>
                  </div>
                  <Button
                    size="sm"
                    variant={team.isActive ? 'default' : 'outline'}
                    onClick={() => handleToggleTeam(team)}
                    disabled={updateTeamStatusMutation.isPending}
                  >
                    {team.isActive ? 'Active' : 'Inactive'}
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground">
              No teams found. Sync teams from the Football Data API first.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
